import { matchGoodsCompositeMapKey } from '@/utils/goodsCompositeMap';
import { compositeImageAPI } from '@/utils/api';

const COMPOSITE_ROWS_CACHE_MAX = 12;
const compositeRowsCacheByTaskId = new Map();
const compositeRowsInFlightByTaskId = new Map();

function touchCompositeRowsCache(taskId, rows) {
  compositeRowsCacheByTaskId.delete(taskId);
  compositeRowsCacheByTaskId.set(taskId, rows);
  while (compositeRowsCacheByTaskId.size > COMPOSITE_ROWS_CACHE_MAX) {
    const oldest = compositeRowsCacheByTaskId.keys().next().value;
    compositeRowsCacheByTaskId.delete(oldest);
  }
}

export function invalidateCompositeRowsCache(taskId) {
  const tid = String(taskId || '').trim();
  if (!tid) return;
  compositeRowsCacheByTaskId.delete(tid);
}

function pickCompositeRowUrl(img) {
  if (!img || typeof img !== 'object') return '';
  return String(
    img.compositeUrl ||
      img.composite_url ||
      img.dataUrl ||
      img.data_url ||
      img.url ||
      '',
  ).trim();
}

function flattenCompositeMapToRows(compositeImagesMap) {
  if (!compositeImagesMap) return [];
  if (Array.isArray(compositeImagesMap)) {
    return compositeImagesMap;
  }
  if (typeof compositeImagesMap !== 'object') return [];
  const rows = [];
  Object.values(compositeImagesMap).forEach((val) => {
    if (Array.isArray(val)) rows.push(...val);
    else if (val && typeof val === 'object') rows.push(val);
  });
  return rows;
}

/** 分页拉取任务下全部合成图（会话缓存 + 并发去重，避免详情页重复打 batch） */
export async function fetchAllCompositeImageRows(taskId, { force = false } = {}) {
  const tid = String(taskId || '').trim();
  if (!tid) return [];

  if (!force && compositeRowsCacheByTaskId.has(tid)) {
    return compositeRowsCacheByTaskId.get(tid);
  }
  if (compositeRowsInFlightByTaskId.has(tid)) {
    return compositeRowsInFlightByTaskId.get(tid);
  }

  const run = (async () => {
    let rows = [];
    if (typeof compositeImageAPI.getAllRowsByTask === 'function') {
      try {
        rows = await compositeImageAPI.getAllRowsByTask(tid);
      } catch (e) {
        console.warn('[商品详情] 分页拉取合成图失败', e);
      }
    }

    touchCompositeRowsCache(tid, rows);
    return rows;
  })();

  compositeRowsInFlightByTaskId.set(tid, run);
  try {
    return await run;
  } finally {
    compositeRowsInFlightByTaskId.delete(tid);
  }
}

function collectRowsForTemplate(flatRows, templateId) {
  const tid = String(templateId ?? '');
  const list = [];
  flatRows.forEach((img) => {
    if (String(img?.templateId ?? img?.template_id ?? '') !== tid) return;
    const url = pickCompositeRowUrl(img);
    if (!url) return;
    const kind = String(img.kind || img.imageKind || '').trim();
    list.push({
      imageId: img.imageId ?? img.image_id,
      compositeUrl: url,
      groupIndex: Number(img.groupIndex ?? img.group_index ?? 0),
      replacedImageGroups: img.replacedImageGroups || {},
      replacedTemplateImageId: img.replacedTemplateImageId || null,
      ...(kind ? { kind } : {}),
    });
  });
  return list;
}

/** 将合成图库数据合并进 goodsForms */
export function mergeCompositeImagesIntoGoodsForms(forms, compositeImagesMapOrRows) {
  const flatRows = Array.isArray(compositeImagesMapOrRows)
    ? compositeImagesMapOrRows
    : flattenCompositeMapToRows(compositeImagesMapOrRows);

  return (forms || []).map((form) => {
    const templateId = String(form.templateId ?? '');
    const fromFlat = collectRowsForTemplate(flatRows, templateId);

    const fromMap = [];
    const map =
      compositeImagesMapOrRows && !Array.isArray(compositeImagesMapOrRows)
        ? compositeImagesMapOrRows
        : {};
    Object.keys(map || {}).forEach((key) => {
      const { ok, groupIndexFromKey } = matchGoodsCompositeMapKey(key, templateId);
      if (!ok) return;
      (map[key] || []).forEach((img) => {
        const url = pickCompositeRowUrl(img);
        if (!url) return;
        const kind = String(img.kind || img.imageKind || '').trim();
        fromMap.push({
          imageId: img.imageId ?? img.image_id,
          compositeUrl: url,
          groupIndex: Number(img.groupIndex ?? img.group_index ?? groupIndexFromKey ?? 0),
          replacedImageGroups: img.replacedImageGroups || {},
          replacedTemplateImageId: img.replacedTemplateImageId || null,
          ...(kind ? { kind } : {}),
        });
      });
    });

    const dedupe = new Map();
    [...fromFlat, ...fromMap].forEach((r) => {
      const k = `${Number(r.groupIndex)}|${r.imageId || ''}|${String(r.compositeUrl).slice(0, 64)}`;
      if (!dedupe.has(k)) dedupe.set(k, r);
    });

    (form.compositeResults || []).forEach((r) => {
      const url = pickCompositeRowUrl(r);
      if (!url || String(url).startsWith('COMP-')) return;
      const gi = Number(r?.groupIndex ?? 0);
      const k = `${gi}|${r.imageId || ''}|${String(url).slice(0, 64)}`;
      const existing = dedupe.get(k);
      if (!existing) {
        dedupe.set(k, { ...r, compositeUrl: url, groupIndex: gi });
        return;
      }
      // 合成图库行可能缺 kind，用 form 内已有 kind 补回
      const existingKind = String(existing.kind || '').trim();
      const formKind = String(r?.kind || '').trim();
      if (!existingKind && formKind) {
        dedupe.set(k, { ...existing, ...r, compositeUrl: existing.compositeUrl || url, groupIndex: gi, kind: formKind });
      }
    });

    (form.compositeResults || []).forEach((r) => {
      const url = pickCompositeRowUrl(r);
      if (url && !String(url).startsWith('COMP-')) return;
      const gi = Number(r?.groupIndex ?? 0);
      const imageId = r?.imageId ?? '';
      const hits = [...dedupe.values()].filter((x) => Number(x.groupIndex) === gi);
      const hit =
        hits.find((x) => imageId && String(x.imageId) === String(imageId)) ||
        (hits.length === 1 ? hits[0] : hits[0]);
      if (!hit) return;
      const k = `${gi}|${hit.imageId || ''}|${String(hit.compositeUrl).slice(0, 64)}`;
      const merged = { ...r, ...hit, groupIndex: gi };
      const kind = String(merged.kind || r?.kind || hit.kind || '').trim();
      if (kind) merged.kind = kind;
      dedupe.set(k, merged);
    });

    return {
      ...form,
      compositeResults: [...dedupe.values()],
    };
  });
}

/** 是否已有可展示的成品 URL */
export function hasDisplayableCompositeForms(forms = []) {
  return (forms || []).some((form) => (
    (form.compositeResults || []).some((row) => {
      const url = pickCompositeRowUrl(row);
      return Boolean(url) && !url.startsWith('COMP-');
    })
  ));
}

/** 拉取并合并合成图（详情页统一入口） */
export function formsNeedCompositeRowHydration(forms = [], taskMeta = {}) {
  if (hasDisplayableCompositeForms(forms)) return false;

  let hasAnyRow = false;
  (forms || []).forEach((form) => {
    if ((form.compositeResults || []).length > 0) hasAnyRow = true;
  });

  const successCount = Number(
    taskMeta.successCount ?? taskMeta.result?.successCount ?? taskMeta.total ?? 0,
  );
  if (successCount > 0) return true;
  return !hasAnyRow ? false : true;
}

export async function loadGoodsFormsWithCompositeImages(taskId, forms, options = {}) {
  const { force = false, taskMeta = null } = options;
  let merged = forms;
  if (force || formsNeedCompositeRowHydration(forms, taskMeta || {})) {
    const rows = await fetchAllCompositeImageRows(taskId);
    merged = mergeCompositeImagesIntoGoodsForms(forms, rows);
  }
  const { ensurePassThroughDetailComposites } = await import('./groupGoodsCompositeResultsByMain');
  merged = await ensurePassThroughDetailComposites(merged);

  // 批量预签展示地址，减少详情首屏逐张 SignedImage 散打 sign-batch
  try {
    const { ensureDisplayImageUrls, warmDisplayCacheFromRecords } = await import('@/utils/imageDisplayAccess');
    const urlRows = [];
    (merged || []).forEach((form) => {
      (form.compositeResults || []).forEach((row) => {
        const url = pickCompositeRowUrl(row);
        if (!url || url.startsWith('COMP-') || url.startsWith('data:')) return;
        urlRows.push({
          url,
          viewUrl: row.viewUrl || row.displayUrl || row.urlDisplay || '',
          displayUrl: row.displayUrl || row.viewUrl || row.urlDisplay || '',
        });
      });
    });
    if (urlRows.length > 0) {
      warmDisplayCacheFromRecords(urlRows, (item) => item.url);
      await ensureDisplayImageUrls(urlRows.map((item) => item.url));
    }
  } catch (error) {
    console.warn('[商品详情] 合成图预签失败', error);
  }

  return merged;
}

function imageGroupHasAssignment(group) {
  if (!group || typeof group !== 'object') return false;
  return Object.values(group).some((value) => {
    if (value == null || value === '') return false;
    if (typeof value === 'object') {
      return Boolean(String(value.id ?? value.imageId ?? value.url ?? '').trim());
    }
    return Boolean(String(value).trim());
  });
}

function stripGoodsFormForListStorage(form) {
  if (!form || typeof form !== 'object') return form;
  const imageGroups = Array.isArray(form.imageGroups) ? form.imageGroups : [];
  return {
    templateId: form.templateId,
    templateName: form.templateName,
    imageGroups,
    compositeResults: [],
  };
}

/** 历史列表内存中不保留成品大图 / 模板全量 JSON，避免刷新合并时卡顿与 OOM */
export function stripHeavyGoodsTaskForListStorage(task) {
  if (!task || task.type !== 'goods') return task;
  const rawForms = task.goodsForms?.length
    ? task.goodsForms
    : (task.result?.goodsForms || []);
  if (!rawForms.length) return task;
  const goodsForms = rawForms.map(stripGoodsFormForListStorage);
  const result = task.result && typeof task.result === 'object'
    ? { ...task.result, goodsForms }
    : task.result;
  return {
    ...task,
    goodsForms,
    result,
  };
}

/**
 * 列表 summary 刷新：仅合并印花分组元数据，不保留 compositeResults / templateData。
 */
export function mergeGoodsFormsForListRefresh(apiForms = [], prevForms = []) {
  const api = Array.isArray(apiForms) ? apiForms : [];
  const prev = Array.isArray(prevForms) ? prevForms : [];
  if (!prev.length) return api.map(stripGoodsFormForListStorage);
  if (!api.length) return prev.map(stripGoodsFormForListStorage);

  const prevByTemplate = new Map(prev.map((form) => [String(form.templateId ?? ''), form]));
  return api.map((form, formIndex) => {
    const prevForm = prevByTemplate.get(String(form.templateId ?? '')) || prev[formIndex];
    if (!prevForm) return stripGoodsFormForListStorage(form);

    const apiGroups = Array.isArray(form.imageGroups) ? form.imageGroups : [];
    const prevGroups = Array.isArray(prevForm.imageGroups) ? prevForm.imageGroups : [];
    const imageGroups = apiGroups.length > 0
      ? apiGroups.map((group, groupIndex) => (
        imageGroupHasAssignment(group) ? group : (prevGroups[groupIndex] || group)
      ))
      : prevGroups;

    return stripGoodsFormForListStorage({
      ...prevForm,
      ...form,
      imageGroups,
    });
  });
}

/**
 * 详情页等场景：列表 summary 缺失时与本地已加载数据合并（含成品）。
 */
export function mergeGoodsFormsPreservingComposites(apiForms = [], prevForms = []) {
  const api = Array.isArray(apiForms) ? apiForms : [];
  const prev = Array.isArray(prevForms) ? prevForms : [];
  if (!prev.length) return api;
  if (!api.length) return prev;

  const prevByTemplate = new Map(prev.map((form) => [String(form.templateId ?? ''), form]));
  return api.map((form, formIndex) => {
    const prevForm = prevByTemplate.get(String(form.templateId ?? '')) || prev[formIndex];
    if (!prevForm) return form;

    const apiGroups = Array.isArray(form.imageGroups) ? form.imageGroups : [];
    const prevGroups = Array.isArray(prevForm.imageGroups) ? prevForm.imageGroups : [];
    const imageGroups = apiGroups.length > 0
      ? apiGroups.map((group, groupIndex) => (
        imageGroupHasAssignment(group) ? group : (prevGroups[groupIndex] || group)
      ))
      : prevGroups;

    const compositeResults = Array.isArray(form.compositeResults) && form.compositeResults.length > 0
      ? form.compositeResults
      : (prevForm.compositeResults || []);

    return {
      ...prevForm,
      ...form,
      imageGroups,
      compositeResults,
    };
  });
}
